import { memo, useState, useEffect } from 'react';
import { trackContactClick } from '@/utils/contactTracking';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Calendar, Gauge, MapPin, Phone, Envelope as Mail, ArrowSquareOut as ExternalLink, ChatText as MessageSquare } from '@phosphor-icons/react';
import { Link } from '@/lib/router-compat';
import { VehicleTypeIcon } from '@/components/shared/VehicleTypeIcon';
import { getVehicleLabel } from '@/utils/vehicleIcons';
import { FavoriteButton } from '@/components/listings/card/FavoriteButton';
import { LeadCaptureForm } from '@/components/leads/LeadCaptureForm';
import { useViewTracking } from '@/hooks/useAnalytics';
import { useTranslationV2 } from '@/hooks/useTranslationV2';

interface QuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: {
    id: string;
    make: string;
    model: string;
    year: number;
    price: number;
    mileage: number;
    location: string;
    images?: string[];
    description?: string;
    fuel_type?: string;
    transmission?: string;
    condition?: string;
    contact_phone?: string;
    contact_email?: string;
    vehicle_type?: string;
    promoted?: boolean;
    auto_promoted?: boolean;
    is_new_listing?: boolean;
  } | null;
}

const QuickViewModalComponent = ({ isOpen, onClose, vehicle }: QuickViewModalProps) => {
  const [showMessageForm, setShowMessageForm] = useState(false);
  const { trackView } = useViewTracking();
  const { t: tVehicles } = useTranslationV2('vehicles');

  useEffect(() => {
    if (isOpen && vehicle?.id) {
      trackView(vehicle.id);
    }
  }, [isOpen, vehicle?.id, trackView]);
  
  if (!vehicle) return null;

  const slug = `${vehicle.make?.toLowerCase().replace(/\s+/g, '-')}-${vehicle.model?.toLowerCase().replace(/\s+/g, '-')}-${vehicle.year}-${vehicle.id.slice(0, 8)}`;
  const title = `${vehicle.make} ${vehicle.model} ${vehicle.year}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold">{title}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                {vehicle.vehicle_type && vehicle.vehicle_type !== 'car' && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <VehicleTypeIcon type={vehicle.vehicle_type} className="h-3 w-3" />
                    {getVehicleLabel(vehicle.vehicle_type)}
                  </Badge>
                )}
                
                {vehicle.auto_promoted && !vehicle.promoted && (
                  <Badge variant="secondary">🚀 Nou 48h</Badge>
                )}
                {vehicle.is_new_listing && !vehicle.promoted && !vehicle.auto_promoted && (
                  <Badge variant="outline" className="bg-green-50 border-green-400 text-green-700">
                    ✨ Nou
                  </Badge>
                )}
              </div>
            </div>
            <FavoriteButton 
              listingId={vehicle.id} 
              title={title}
              size="md"
            />
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image Gallery */}
          <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
            {vehicle.images && vehicle.images.length > 0 ? (
              <OptimizedImage
                src={vehicle.images[0]}
                alt={title}
                className="w-full h-full object-cover"
                width={800}
                height={450}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                {tVehicles('no_image')}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-primary">
                {new Intl.NumberFormat('ro-RO').format(vehicle.price)} €
              </p>
              <p className="text-sm text-muted-foreground mt-1">{tVehicles('price_negotiable')}</p>
            </div>
          </div>

          {/* Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{vehicle.year}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              <span>{new Intl.NumberFormat('ro-RO').format(vehicle.mileage)} km</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{vehicle.location}</span>
            </div>
            {vehicle.fuel_type && (
              <div className="text-sm">
                <span className="font-medium">{tVehicles('fuel_label')}:</span> {vehicle.fuel_type}
              </div>
            )}
          </div>

          {/* Additional Details */}
          {(vehicle.transmission || vehicle.condition) && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              {vehicle.transmission && (
                <div>
                  <span className="font-medium">{tVehicles('transmission_label')}:</span> {vehicle.transmission}
                </div>
              )}
              {vehicle.condition && (
                <div>
                  <span className="font-medium">{tVehicles('condition_label')}:</span> {vehicle.condition}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {vehicle.description && (
            <div>
              <h3 className="font-semibold mb-2">{tVehicles('description')}</h3>
              <p className="text-sm text-muted-foreground line-clamp-3">
                {vehicle.description}
              </p>
            </div>
          )}

          {/* Contact Actions */}
          {!showMessageForm ? (
            <div className="flex flex-col sm:flex-row gap-3">
              {vehicle.contact_phone && (
                <Button className="flex-1 gap-2" asChild>
                  <a href={`tel:${vehicle.contact_phone}`} data-seller-contact onClick={() => trackContactClick({ id: vehicle.id, sellerId: (vehicle as any).user_id, sellerType: (vehicle as any).seller_type }, 'phone', 'quick_view')}>
                    <Phone className="h-4 w-4" />
                    {tVehicles('contact_dealer_button')}
                  </a>
                </Button>
              )}
              <Button 
                variant="outline" 
                className="flex-1 gap-2"
                onClick={() => setShowMessageForm(true)}
              >
                <MessageSquare className="h-4 w-4" />
                {tVehicles('send_message')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{tVehicles('send_message_dealer')}</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowMessageForm(false)}
                >
                  {tVehicles('back')}
                </Button>
              </div>
              <LeadCaptureForm 
                variant="quick" 
                source={`quick-view-${vehicle.id}`}
                title="Contactează dealer"
                subtitle={`Mesaj pentru: ${title}`}
                defaultLeadType="buyer"
                className="border-0 shadow-none"
                listingId={vehicle.id}
              />
            </div>
          )}

          {/* View Full Details */}
          <Button variant="secondary" className="w-full gap-2" asChild>
            <Link to={`/cars/details/${vehicle.id}`}>
              <ExternalLink className="h-4 w-4" />
              {tVehicles('view_details')}
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const QuickViewModal = memo(QuickViewModalComponent);
